import type { SpreadsheetRangeSelectionDescriptor } from '@/components/spreadsheet/Spreadsheet/types'

import type { ViewportViewerState } from '@/types/chat-v2/sessions'

/**
 * TypeScript twin of the citation locator DSL owned by
 * `sweetspot/core/core/markit/anchors.py` (markit-py design doc, Appendix A).
 * Canonical wire strings only (`FileSource.locator`); the inline markdown
 * short forms (`{Costs!A5:E5}`) are emission-side and stay in Python.
 *
 * Grammar parity is enforced by `locator.conformance.json`, the corpus shared
 * with the Python suite: every valid locator parses to identical fields and
 * re-renders byte-identically; every invalid string is rejected.
 */
export type Locator =
  /** `pdf:p12` — 1-based page; `pdf:p4-p6` — inclusive page span. */
  | { kind: 'page'; page: number; endPage?: number }
  /** `xlsx:'Q3 Costs'!B2:D7` — sheet + 1-based A1 range. */
  | { kind: 'sheet'; sheetName: string; cellRange: string }
  /** `pptx:s3/sh14` — 1-based slide, optional shape by `p:cNvPr @id`. */
  | { kind: 'slide'; slideIndex: number; shapeId?: number }
  /** `docx:para(1A2B3C4D)` (durable) or `docx:p42` (0-based body ordinal). */
  | { kind: 'paragraph'; paragraphIndex?: number; paragraphId?: string }

export class LocatorParseError extends Error {}

const A1_RANGE = /^[A-Z]{1,3}[1-9][0-9]*(?::[A-Z]{1,3}[1-9][0-9]*)?$/
const PARA_ID = /^[0-9A-F]{8}$/
const CANON_PAGE = /^pdf:p([1-9][0-9]*)$/
// A span's shortest spelling is canonical: `p4-p4` is spelled `p4`, and a
// backwards span has no spelling at all — both are rejected on parse.
const CANON_PAGE_RANGE = /^pdf:p([1-9][0-9]*)-p([1-9][0-9]*)$/
const CANON_SHEET =
  /^xlsx:'((?:[^']|'')+)'!([A-Z]{1,3}[1-9][0-9]*(?::[A-Z]{1,3}[1-9][0-9]*)?)$/
// Zero is a legal shape id / paragraph index, but leading zeros are not
// canonical: `sh014` would re-render as `sh14` and break byte identity.
const CANON_SLIDE = /^pptx:s([1-9][0-9]*)(?:\/sh(0|[1-9][0-9]*))?$/
const CANON_PARA_INDEX = /^docx:p(0|[1-9][0-9]*)$/
const CANON_PARA_ID = /^docx:para\(([0-9A-F]{8})\)$/

const quoteSheet = (name: string) => `'${name.replaceAll("'", "''")}'`

export const renderLocator = (locator: Locator): string => {
  switch (locator.kind) {
    case 'page': {
      if (!Number.isInteger(locator.page) || locator.page < 1)
        throw new LocatorParseError(`invalid page: ${locator.page}`)
      if (locator.endPage === undefined) return `pdf:p${locator.page}`
      if (!Number.isInteger(locator.endPage) || locator.endPage <= locator.page)
        throw new LocatorParseError(
          `invalid page span: ${locator.page}-${locator.endPage}`,
        )
      return `pdf:p${locator.page}-p${locator.endPage}`
    }
    case 'sheet': {
      if (!locator.sheetName || !A1_RANGE.test(locator.cellRange))
        throw new LocatorParseError(
          `sheet locator requires a sheet name and an A1 range: ${locator.cellRange}`,
        )
      return `xlsx:${quoteSheet(locator.sheetName)}!${locator.cellRange}`
    }
    case 'slide': {
      if (!Number.isInteger(locator.slideIndex) || locator.slideIndex < 1)
        throw new LocatorParseError(`invalid slide: ${locator.slideIndex}`)
      const base = `pptx:s${locator.slideIndex}`
      return locator.shapeId !== undefined
        ? `${base}/sh${locator.shapeId}`
        : base
    }
    case 'paragraph': {
      if (locator.paragraphId !== undefined) {
        if (!PARA_ID.test(locator.paragraphId))
          throw new LocatorParseError(`invalid paraId: ${locator.paragraphId}`)
        return `docx:para(${locator.paragraphId})`
      }
      if (
        locator.paragraphIndex === undefined ||
        !Number.isInteger(locator.paragraphIndex) ||
        locator.paragraphIndex < 0
      )
        throw new LocatorParseError(
          'paragraph locator requires paragraphId or paragraphIndex',
        )
      return `docx:p${locator.paragraphIndex}`
    }
  }
}

export const parseLocator = (text: string): Locator => {
  const trimmed = text.trim()
  let m = CANON_PAGE.exec(trimmed)
  if (m) return { kind: 'page', page: Number(m[1]) }
  m = CANON_PAGE_RANGE.exec(trimmed)
  if (m) {
    const page = Number(m[1])
    const endPage = Number(m[2])
    if (endPage <= page)
      throw new LocatorParseError(`invalid page span: ${trimmed}`)
    return { kind: 'page', page, endPage }
  }
  m = CANON_SHEET.exec(trimmed)
  if (m)
    return {
      kind: 'sheet',
      sheetName: m[1].replaceAll("''", "'"),
      cellRange: m[2],
    }
  m = CANON_SLIDE.exec(trimmed)
  if (m)
    return {
      kind: 'slide',
      slideIndex: Number(m[1]),
      ...(m[2] !== undefined ? { shapeId: Number(m[2]) } : {}),
    }
  m = CANON_PARA_ID.exec(trimmed)
  if (m) return { kind: 'paragraph', paragraphId: m[1] }
  m = CANON_PARA_INDEX.exec(trimmed)
  if (m) return { kind: 'paragraph', paragraphIndex: Number(m[1]) }
  throw new LocatorParseError(`unrecognized locator: ${JSON.stringify(text)}`)
}

/**
 * Derives the canonical locator for the location a viewport selection points
 * at, or null when the surface is not addressable yet (text viewers,
 * spreadsheet shapes — no ids on the wire, or Word selections without a
 * reported paragraph id).
 */
export const buildLocatorFromViewerState = (
  viewer: ViewportViewerState | undefined,
): string | null => {
  if (!viewer) return null
  switch (viewer.kind) {
    case 'pdf': {
      const page = viewer.selection_start_page ?? viewer.page
      if (page === undefined || page < 1) return null
      // A selection that runs onto later pages carries its whole span; the
      // visible-page fallback has no selection, so no span either.
      const endPage = viewer.selection_end_page
      return renderLocator({
        kind: 'page',
        page,
        ...(viewer.selection_start_page !== undefined &&
        endPage !== undefined &&
        endPage > page
          ? { endPage }
          : {}),
      })
    }
    case 'spreadsheet': {
      const selection = viewer.selection
      if (
        selection?.kind !== 'range' ||
        !viewer.sheet_name ||
        !A1_RANGE.test(selection.a1)
      )
        return null
      return renderLocator({
        kind: 'sheet',
        sheetName: viewer.sheet_name,
        cellRange: selection.a1,
      })
    }
    case 'presentation': {
      // The selected objects' own slides, not the tracked visible slide —
      // the latter can lag navigation (the "Slide 1" pill bug).
      const objectSlides = (viewer.selected_objects ?? [])
        .map((group) => group.slide_number)
        .filter((slide) => Number.isInteger(slide) && slide >= 1)
      const slide =
        objectSlides.length > 0
          ? Math.min(...objectSlides)
          : viewer.slide_number
      if (slide === undefined || slide < 1) return null
      return renderLocator({ kind: 'slide', slideIndex: slide })
    }
    case 'text':
      return null
    case 'docx': {
      // Guard before rendering — renderLocator throws on a malformed id.
      const paraId = viewer.selection_paragraph_id
      if (!paraId || !PARA_ID.test(paraId)) return null
      return renderLocator({ kind: 'paragraph', paragraphId: paraId })
    }
  }
}

/** `B` → 2. The A1 grammar caps columns at 3 letters, so this cannot overflow. */
const columnIndexFromLabel = (label: string): number => {
  let index = 0
  for (const char of label) index = index * 26 + (char.charCodeAt(0) - 64)
  return index
}

const A1_CELL = /^([A-Z]{1,3})([1-9][0-9]*)$/

/**
 * Resolves a sheet locator (`xlsx:'Pricing'!B2:D9`) back into the viewer's
 * selection descriptor, so opening a selection card can re-select the exact
 * range. Null for non-sheet or unparseable locators — the caller falls back
 * to a plain open.
 */
export const spreadsheetSelectionFromLocator = (
  text: string,
): SpreadsheetRangeSelectionDescriptor | null => {
  let locator: Locator
  try {
    locator = parseLocator(text)
  } catch (error) {
    if (error instanceof LocatorParseError) return null
    throw error
  }
  if (locator.kind !== 'sheet') return null
  const [startRef, endRef = startRef] = locator.cellRange.split(':')
  const start = startRef.match(A1_CELL)
  const end = endRef.match(A1_CELL)
  if (!start || !end) return null
  const startRow = Number(start[2])
  const startColumn = columnIndexFromLabel(start[1])
  const endRow = Number(end[2])
  const endColumn = columnIndexFromLabel(end[1])
  return {
    kind: 'range',
    source: 'grid',
    sheetName: locator.sheetName,
    selectionKind: 'cell',
    coordinateBase: 1,
    range: {
      startRow: Math.min(startRow, endRow),
      startColumn: Math.min(startColumn, endColumn),
      endRow: Math.max(startRow, endRow),
      endColumn: Math.max(startColumn, endColumn),
    },
  }
}
